// ── useGoogleCalendar ─────────────────────────────────────────────────────────
// React hook that owns the Google Calendar *connection* (not the events — those
// stay in App state). Handles silent reconnect on load, the calendar list, the
// chosen target calendar, and a guarded `syncNow` the app can call on demand or
// whenever local events change.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import {
  isGoogleCalendarConfigured,
  isGoogleTasksConfigured,
  checkConnection,
  connect as gcalConnect,
  disconnect as gcalDisconnect,
  finishNativeConnect,
} from './calendar';
import { runSync, listCalendars, resetTrack, mergeSyncedEvents, mergeSyncedOverrides } from './sync';
import { runTasksSync, listTaskLists, resetTaskTrack } from './tasksSync';

const NATIVE_CALLBACK_PREFIX = 'com.clarity.app://gcal-callback';

const CAL_KEY = 'clarity_gcalCalendarId';
const EVENTS_KEY = 'clarity_calEvents';
const OVERRIDES_KEY = 'clarity_calEventOccOverrides';

// The saved copy is shared by every tab of the app, so it's the newest data
// even when this tab's own copy has fallen behind another tab's edits.
function readSaved(key, fallback) {
  try {
    const v = JSON.parse(localStorage.getItem(key));
    if (Array.isArray(fallback) ? Array.isArray(v) : (v && typeof v === 'object' && !Array.isArray(v))) return v;
  } catch {}
  return fallback;
}

// Only one tab syncs with Google at a time (browsers without Web Locks just run).
function withSyncLock(fn) {
  const locks = typeof navigator !== 'undefined' && navigator.locks;
  if (!locks || typeof locks.request !== 'function') return fn();
  return locks.request('clarity-gcal-sync', { ifAvailable: true }, (lock) => (lock ? fn() : undefined));
}
const TASKLIST_KEY = 'clarity_gtaskListId';

// Turn the OAuth error code the backend echoes back (Google's own short code,
// e.g. from a failed /api/google/callback exchange) into something a human
// can act on. Falls back to a generic message for anything unrecognized.
function friendlyConnectError(reason) {
  if (reason === 'access_denied') return 'Google sign-in was cancelled before finishing.';
  if (reason === 'state_mismatch') return 'That connection attempt expired. Please try again.';
  if (reason === 'invalid_grant' || reason === 'unauthorized_client' || reason === 'no_refresh_token') {
    return 'Google declined to connect this account. Try a different Google account, or try again in a bit.';
  }
  return `Could not connect to Google Calendar. Please try again.${reason ? ` (${reason})` : ''}`;
}

// Turn a raw Google Tasks API error into something a human can act on.
function friendlyGtaskError(e) {
  const status = e?.status;
  const msg = String(e?.message || '');
  if (status === 404 || /not found/i.test(msg)) {
    return 'That task list was not found. Switched back to your default list.';
  }
  if (status === 403 || /access ?not ?configured|not been used|disabled|insufficient/i.test(msg)) {
    return 'Task sync needs one more permission. Tap Disconnect, then Connect again.';
  }
  if (status === 401 || /invalid|unauthor/i.test(msg)) {
    return 'Google sign-in expired. Tap Reconnect to continue syncing.';
  }
  return msg || 'Task sync failed. Will retry.';
}

// Turn a raw Google API error into something a human can act on.
function friendlyGcalError(e) {
  const status = e?.status;
  const msg = String(e?.message || '');
  if (status === 404 || /not found/i.test(msg)) {
    return 'That calendar was not found. Switched back to your primary calendar.';
  }
  if (status === 403 || /access ?not ?configured|not been used|disabled/i.test(msg)) {
    return 'Google Calendar access isn’t enabled for this app yet. Try reconnecting.';
  }
  if (status === 401 || /invalid|unauthor/i.test(msg)) {
    return 'Google sign-in expired. Tap Reconnect to continue syncing.';
  }
  return msg || 'Sync failed. Will retry.';
}

export function useGoogleCalendar() {
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [calendars, setCalendars] = useState([]);
  const [calendarId, setCalendarIdState] = useState(() => {
    try { return localStorage.getItem(CAL_KEY) || 'primary'; } catch { return 'primary'; }
  });
  const [status, setStatus] = useState('idle');   // idle | syncing | error
  const [error, setError] = useState('');
  const [lastSyncAt, setLastSyncAt] = useState(null);
  const [lastStats, setLastStats] = useState(null);

  const [taskLists, setTaskLists] = useState([]);
  const [taskListId, setTaskListIdState] = useState(() => {
    try { return localStorage.getItem(TASKLIST_KEY) || ''; } catch { return ''; }
  });
  const [taskStatus, setTaskStatus] = useState('idle'); // idle | syncing | error
  const [taskError, setTaskError] = useState('');
  const [lastTaskSyncAt, setLastTaskSyncAt] = useState(null);
  const [lastTaskStats, setLastTaskStats] = useState(null);

  const syncing = useRef(false); // prevents overlapping event syncs
  const taskSyncing = useRef(false); // prevents overlapping task syncs

  const setCalendarId = useCallback((id) => {
    setCalendarIdState(id);
    try { localStorage.setItem(CAL_KEY, id); } catch {}
    // Switching calendars invalidates the per-calendar tracking set.
    resetTrack();
  }, []);

  const setTaskListId = useCallback((id) => {
    setTaskListIdState(id);
    try { localStorage.setItem(TASKLIST_KEY, id); } catch {}
    // Switching lists invalidates the per-list tracking set.
    resetTaskTrack();
  }, []);

  const refreshCalendars = useCallback(async () => {
    try {
      const cals = await listCalendars();
      setCalendars(cals);
      // If the saved calendar no longer exists, fall back to primary.
      const haveSaved = cals.some((c) => c.id === calendarId);
      if (!haveSaved) {
        const primary = cals.find((c) => c.primary);
        if (primary) setCalendarId(primary.id);
      }
    } catch (e) {
      setError(e.message || 'Could not load calendars');
    }
  }, [calendarId, setCalendarId]);

  const refreshTaskLists = useCallback(async () => {
    if (!isGoogleTasksConfigured) return;
    try {
      const lists = await listTaskLists();
      setTaskLists(lists);
      // If nothing's chosen yet, or the saved list no longer exists, fall
      // back to the first (Google Tasks always has at least a default list).
      const haveSaved = lists.some((l) => l.id === taskListId);
      if (!haveSaved && lists.length) setTaskListId(lists[0].id);
    } catch (e) {
      // Missing-scope 403s are expected until the user reconnects (see
      // friendlyGtaskError) — don't surface those as a startup error.
      if (e?.status !== 403) setTaskError(e.message || 'Could not load task lists');
    }
  }, [taskListId, setTaskListId]);

  // Restore the connection on mount with no prompt. The refresh token lives on
  // the server, so a single backend call mints a fresh access token — the
  // connection is permanent across reloads with zero UI. Also strips the
  // ?gcal=connected|error marker the OAuth callback leaves on the URL.
  useEffect(() => {
    let alive = true;
    if (!isGoogleCalendarConfigured) return;

    let justReturned = null;
    let reason = null;
    try {
      const sp = new URLSearchParams(window.location.search);
      justReturned = sp.get('gcal');
      reason = sp.get('gcal_reason');
      if (justReturned) {
        sp.delete('gcal');
        sp.delete('gcal_reason');
        const qs = sp.toString();
        window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash);
      }
    } catch { /* ignore */ }

    if (justReturned === 'error') setError(friendlyConnectError(reason));

    (async () => {
      const ok = await checkConnection();
      if (alive && ok) { setConnected(true); refreshCalendars(); refreshTaskLists(); }
    })();
    return () => { alive = false; };
  }, [refreshCalendars, refreshTaskLists]);

  // Native only: the OAuth hop happens in the system browser (see connect()
  // below), which lands back in the app via a custom-scheme deep link rather
  // than the ?gcal= query param the web flow uses. finishNativeConnect() hands
  // the encrypted refresh token to the backend so it can re-issue it as a
  // cookie the app's own WebView can see.
  useEffect(() => {
    if (!isGoogleCalendarConfigured || !Capacitor.isNativePlatform()) return;
    let alive = true;
    const listenerPromise = CapacitorApp.addListener('appUrlOpen', ({ url }) => {
      if (!url || !url.startsWith(NATIVE_CALLBACK_PREFIX)) return;
      Browser.close().catch(() => {});
      const params = new URL(url).searchParams;
      const status = params.get('status');
      const rt = params.get('rt');
      const reason = params.get('reason');
      (async () => {
        if (status === 'connected' && rt) {
          try {
            await finishNativeConnect(rt);
            if (!alive) return;
            setConnected(true);
            setConnecting(false);
            refreshCalendars();
            refreshTaskLists();
            return;
          } catch { /* fall through to error below */ }
        }
        if (!alive) return;
        setError(friendlyConnectError(reason));
        setConnecting(false);
      })();
    });
    return () => { alive = false; listenerPromise.then((h) => h.remove()); };
  }, [refreshCalendars, refreshTaskLists]);

  // Web: full-page redirect to Google consent — the browser navigates away and
  // returns to ?gcal=connected, where the mount effect picks up the live
  // connection. Native: opens the system browser instead (see calendar.js);
  // the effect above finishes the job when it hands control back.
  const connect = useCallback(() => {
    setConnecting(true);
    setError('');
    gcalConnect();
  }, []);

  const disconnect = useCallback(async () => {
    await gcalDisconnect();
    resetTrack();
    resetTaskTrack();
    // Drop the remembered calendar/list too. Their IDs belong to the account
    // being disconnected (a primary calendar's ID *is* that account's email),
    // so keeping them means the next account to connect would sync against a
    // calendar it doesn't own.
    setCalendarIdState('primary');
    setTaskListIdState('');
    try { localStorage.removeItem(CAL_KEY); } catch {}
    try { localStorage.removeItem(TASKLIST_KEY); } catch {}
    setConnected(false);
    setCalendars([]);
    setStatus('idle');
    setLastStats(null);
    setTaskLists([]);
    setTaskStatus('idle');
    setLastTaskStats(null);
  }, []);

  // Stops task syncing without touching the shared Calendar connection — Tasks
  // rides the same OAuth token as Calendar (see lib/google-oauth.js), so there's
  // no separate grant to revoke on Google's side. This just clears the chosen
  // list and tracking state so Clarity stops pushing/pulling tasks; the app can
  // still sync Calendar events, and tapping "Connect" under Tasks again just
  // re-picks a list (no new consent needed).
  const disconnectTasks = useCallback(() => {
    setTaskListId('');
    setTaskLists([]);
    setTaskStatus('idle');
    setTaskError('');
    setLastTaskStats(null);
    setLastTaskSyncAt(null);
  }, [setTaskListId]);

  // syncNow(calEvents, setCalEvents, occurrenceOverrides, setOccurrenceOverrides):
  // reconcile events and per-occurrence overrides in both directions.
  const syncNow = useCallback(async (calEvents, setCalEvents, occurrenceOverrides, setOccurrenceOverrides) => {
    if (!connected || syncing.current) return;
    syncing.current = true;
    const run = async () => {
      setStatus('syncing');
      setError('');
      try {
        // Sync from the saved copy, not this tab's possibly stale one: a tab that
        // missed an event added in another tab would otherwise read it as "deleted
        // here" and delete it from Google too.
        const startEvents = readSaved(EVENTS_KEY, calEvents);
        const startOverrides = readSaved(OVERRIDES_KEY, occurrenceOverrides || {});
        const { events, occurrenceOverrides: nextOverrides, stats, changed } = await runSync(startEvents, calendarId, startOverrides);
        if (changed) {
          // Apply only what the sync changed, onto whatever is current now, so an
          // edit made while it ran isn't overwritten.
          setCalEvents((latest) => mergeSyncedEvents(startEvents, events, latest));
          if (setOccurrenceOverrides) setOccurrenceOverrides((latest) => mergeSyncedOverrides(startOverrides, nextOverrides, latest || {}));
        }
        setLastStats(stats);
        setLastSyncAt(Date.now());
        setStatus('idle');
      } catch (e) {
        // A 404 on a non-primary calendar means the saved calendar no longer
        // exists (e.g. deleted, or it belonged to a different Google account).
        // Self-heal by falling back to the always-present primary calendar and
        // clearing the error — the next sync will use primary cleanly.
        if (e?.status === 404 && calendarId !== 'primary') {
          setCalendarId('primary');
          setError('');
          setStatus('idle');
          refreshCalendars();
        } else {
          setError(friendlyGcalError(e));
          setStatus('error');
        }
      }
    };
    try {
      await withSyncLock(run);
    } finally {
      syncing.current = false;
    }
  }, [connected, calendarId, setCalendarId, refreshCalendars]);

  // syncTasksNow(tasks, setTasks): reconcile non-recurring tasks in both
  // directions against the chosen Google Tasks list.
  const syncTasksNow = useCallback(async (tasks, setTasks) => {
    if (!isGoogleTasksConfigured || !connected || !taskListId || taskSyncing.current) return;
    taskSyncing.current = true;
    setTaskStatus('syncing');
    setTaskError('');
    try {
      const { tasks: nextTasks, stats, changed } = await runTasksSync(tasks, taskListId);
      if (changed) setTasks(nextTasks);
      setLastTaskStats(stats);
      setLastTaskSyncAt(Date.now());
      setTaskStatus('idle');
    } catch (e) {
      // A 404 on a non-default list means the saved list no longer exists.
      // Self-heal by falling back to whatever's first and clearing the error.
      if (e?.status === 404 && taskLists.length) {
        setTaskListId(taskLists[0].id);
        setTaskError('');
        setTaskStatus('idle');
      } else {
        setTaskError(friendlyGtaskError(e));
        setTaskStatus('error');
      }
    } finally {
      taskSyncing.current = false;
    }
  }, [connected, taskListId, taskLists, setTaskListId]);

  return {
    configured: isGoogleCalendarConfigured,
    connected,
    connecting,
    calendars,
    calendarId,
    setCalendarId,
    status,
    error,
    lastSyncAt,
    lastStats,
    connect,
    disconnect,
    syncNow,
    tasksConfigured: isGoogleTasksConfigured,
    taskLists,
    taskListId,
    setTaskListId,
    taskStatus,
    taskError,
    lastTaskSyncAt,
    lastTaskStats,
    syncTasksNow,
    disconnectTasks,
  };
}
